import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, CheckCircle2, Loader2, Trash2, Plus, AlertTriangle,
  Building2, Package, FileText, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLANK_ITEM = {
  product_name: '', description: '', sku: '',
  quantity: 1, unit: 'units', unit_price: 0,
  currency: 'AED', tax_rate: 0, discount: 0,
  subtotal: 0, total: 0,
};

const BLANK_SUPPLIER = {
  company_name: '', contact_name: '', email: '', phone: '',
  address: '', tax_id: '', trn: '', license_number: '', country: '',
};

const TextField = ({ label, value, onChange, type = 'text', testId, ...rest }) => (
  <div>
    <label className="text-[11px] font-semibold text-gray-500 uppercase">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
      data-testid={testId}
      {...rest}
    />
  </div>
);

const SupplierOCRDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');

  // Optional draft-PO creation
  const [createDraftPO, setCreateDraftPO] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [poCompanyId, setPoCompanyId] = useState('');
  const [poBankAccountId, setPoBankAccountId] = useState('');
  const [poDeliveryAddress, setPoDeliveryAddress] = useState('');

  // Dynamically load bank accounts when company changes (endpoint requires ?company_id=)
  useEffect(() => {
    if (!poCompanyId) { setBanks([]); return; }
    axios
      .get(`${API_URL}/api/procurement/bank-accounts?company_id=${poCompanyId}`, { headers: authHeaders })
      .then((r) => setBanks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBanks([]));
  }, [poCompanyId]); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rDoc, rWh, rCo] = await Promise.all([
        axios.get(`${API_URL}/api/procurement/supplier-ocr/${id}`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/inventory/warehouses`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/procurement/companies`, { headers: authHeaders }).catch(() => ({ data: [] })),
      ]);
      const d = rDoc.data;
      d.supplier = { ...BLANK_SUPPLIER, ...(d.supplier || {}) };
      d.line_items = (d.line_items || []).map((li) => ({ ...BLANK_ITEM, ...li }));
      d.totals = d.totals || { subtotal: 0, tax_total: 0, discount_total: 0, grand_total: 0, currency: 'AED' };
      setDoc(d);
      setWarehouses(Array.isArray(rWh.data) ? rWh.data : []);
      setCompanies(Array.isArray(rCo.data) ? rCo.data : []);
    } catch (err) {
      toast({
        variant: 'destructive', title: 'Error',
        description: err.response?.data?.detail || 'Failed to load document',
      });
      navigate('/admin/procurement/ocr');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ---- mutators ----
  const setField = (path, value) => {
    setDoc((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = { ...ref[parts[i]] };
        ref = ref[parts[i]];
      }
      ref[parts.at(-1)] = value;
      return next;
    });
  };

  const setItem = (idx, key, value) => {
    setDoc((prev) => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [key]: value };
      // live recompute subtotal/total
      const qty = Number(items[idx].quantity) || 0;
      const price = Number(items[idx].unit_price) || 0;
      const taxRate = Number(items[idx].tax_rate) || 0;
      const discount = Number(items[idx].discount) || 0;
      const subtotal = qty * price;
      const total = subtotal + (subtotal * taxRate / 100) - discount;
      items[idx].subtotal = Number(subtotal.toFixed(2));
      items[idx].total = Number(total.toFixed(2));
      return { ...prev, line_items: items };
    });
  };

  const addItem = () => setDoc((p) => ({ ...p, line_items: [...p.line_items, { ...BLANK_ITEM }] }));
  const removeItem = (idx) => setDoc((p) => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));

  const recomputeTotals = () => {
    if (!doc) return { subtotal: 0, tax: 0, discount: 0, grand: 0 };
    const subtotal = doc.line_items.reduce((s, i) => s + (Number(i.subtotal) || 0), 0);
    const tax = doc.line_items.reduce((s, i) => s + (Number(i.subtotal) * Number(i.tax_rate || 0) / 100), 0);
    const discount = doc.line_items.reduce((s, i) => s + (Number(i.discount) || 0), 0);
    const grand = subtotal + tax - discount;
    return { subtotal, tax, discount, grand };
  };

  // ---- actions ----
  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const payload = {
        document_type: doc.document_type,
        document_number: doc.document_number,
        issue_date: doc.issue_date,
        due_date: doc.due_date,
        payment_terms: doc.payment_terms,
        notes: doc.notes,
        supplier: doc.supplier,
        line_items: doc.line_items,
        totals: doc.totals,
        status: doc.status === 'parsed' ? 'reviewed' : doc.status,
      };
      const r = await axios.patch(`${API_URL}/api/procurement/supplier-ocr/${id}`, payload, { headers: authHeaders });
      setDoc({
        ...r.data,
        supplier: { ...BLANK_SUPPLIER, ...(r.data.supplier || {}) },
        line_items: (r.data.line_items || []).map((li) => ({ ...BLANK_ITEM, ...li })),
      });
      toast({ title: 'Saved', description: 'Changes persisted.' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Error', description: err.response?.data?.detail || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDoc = async () => {
    if (!doc) return;
    if (!doc.supplier.company_name?.trim()) {
      toast({ variant: 'destructive', title: 'Missing supplier', description: 'Supplier name is required.' });
      return;
    }
    if (createDraftPO && (!poCompanyId || !poBankAccountId)) {
      toast({ variant: 'destructive', title: 'PO setup required', description: 'Select a company and bank account to create the draft PO.' });
      return;
    }
    const msg = createDraftPO
      ? 'Confirm will create Supplier (if new), Inventory items AND a Draft Purchase Order. Continue?'
      : 'Confirm will create Supplier (if new) and add Inventory items. Continue?';
    if (!window.confirm(msg)) return;
    setConfirming(true);
    try {
      await save();
      const payload = {
        warehouse_id: warehouseId || null,
        create_inventory: true,
        create_supplier_if_missing: true,
        create_draft_po: createDraftPO,
        po_company_id: createDraftPO ? poCompanyId : null,
        po_bank_account_id: createDraftPO ? poBankAccountId : null,
        po_delivery_address: createDraftPO ? (poDeliveryAddress || null) : null,
      };
      const r = await axios.post(`${API_URL}/api/procurement/supplier-ocr/${id}/confirm`, payload, { headers: authHeaders });
      setDoc({
        ...r.data,
        supplier: { ...BLANK_SUPPLIER, ...(r.data.supplier || {}) },
        line_items: (r.data.line_items || []).map((li) => ({ ...BLANK_ITEM, ...li })),
      });
      const poInfo = r.data.created_po_number ? ` + Draft PO ${r.data.created_po_number}` : '';
      toast({
        title: 'Confirmed',
        description: `${r.data.created_inventory_ids?.length || 0} inventory items created${poInfo}.`,
      });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Confirm failed', description: err.response?.data?.detail || 'Failed' });
    } finally {
      setConfirming(false);
    }
  };

  const reparse = async () => {
    if (!doc) return;
    setReparsing(true);
    try {
      const r = await axios.post(`${API_URL}/api/procurement/supplier-ocr/${id}/reparse`, {}, { headers: authHeaders });
      setDoc({
        ...r.data,
        supplier: { ...BLANK_SUPPLIER, ...(r.data.supplier || {}) },
        line_items: (r.data.line_items || []).map((li) => ({ ...BLANK_ITEM, ...li })),
      });
      if (r.data.status === 'failed') {
        toast({ variant: 'destructive', title: 'Still failing', description: r.data.parse_error || 'Retry later' });
      } else {
        toast({ title: 'Reparsed', description: `${r.data.line_items?.length || 0} line items extracted.` });
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Reparse failed', description: err.response?.data?.detail || 'Failed' });
    } finally {
      setReparsing(false);
    }
  };

  if (loading || !doc) {
    return (
      <div className="p-12 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const totals = recomputeTotals();
  const locked = doc.status === 'confirmed';

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ocr-detail-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin/procurement/ocr" className="text-sm text-gray-500 flex items-center gap-1 mb-2 hover:text-gray-800">
            <ArrowLeft className="w-4 h-4" /> Back to list
          </Link>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <FileText className="h-6 w-6 text-[#D97706]" /> {doc.file_name}
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Parsed by {doc.llm_model || '—'} · Status: <span className="font-semibold">{doc.status}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {doc.status === 'failed' && (
            <Button
              variant="outline"
              onClick={reparse}
              disabled={reparsing}
              className="border-amber-300 text-amber-700"
              data-testid="reparse-btn"
            >
              {reparsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Retry Parse
            </Button>
          )}
          <Button variant="outline" onClick={save} disabled={saving || locked} data-testid="save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={confirmDoc}
            disabled={confirming || locked}
            data-testid="confirm-btn"
          >
            {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {locked ? 'Confirmed' : 'Confirm & Persist'}
          </Button>
        </div>
      </div>

      {doc.parse_error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4" /> Parse error: {doc.parse_error}
        </div>
      )}

      {/* Document meta */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Document</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TextField label="Document Type" value={doc.document_type}
            onChange={(v) => setField('document_type', v)} testId="field-doc-type" />
          <TextField label="Document Number" value={doc.document_number}
            onChange={(v) => setField('document_number', v)} testId="field-doc-number" />
          <TextField label="Issue Date" type="date" value={(doc.issue_date || '').slice(0, 10)}
            onChange={(v) => setField('issue_date', v)} testId="field-issue-date" />
          <TextField label="Due Date" type="date" value={(doc.due_date || '').slice(0, 10)}
            onChange={(v) => setField('due_date', v)} testId="field-due-date" />
          <TextField label="Payment Terms" value={doc.payment_terms}
            onChange={(v) => setField('payment_terms', v)} testId="field-payment-terms" />
          <div className="md:col-span-3">
            <label className="text-[11px] font-semibold text-gray-500 uppercase">Notes</label>
            <textarea
              value={doc.notes || ''}
              onChange={(e) => setField('notes', e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="field-notes"
            />
          </div>
        </CardContent>
      </Card>

      {/* Supplier */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Supplier (auto-detected)
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField label="Company Name *" value={doc.supplier.company_name}
            onChange={(v) => setField('supplier.company_name', v)} testId="field-sup-name" />
          <TextField label="Contact Name" value={doc.supplier.contact_name}
            onChange={(v) => setField('supplier.contact_name', v)} testId="field-sup-contact" />
          <TextField label="Email" value={doc.supplier.email}
            onChange={(v) => setField('supplier.email', v)} testId="field-sup-email" />
          <TextField label="Phone" value={doc.supplier.phone}
            onChange={(v) => setField('supplier.phone', v)} testId="field-sup-phone" />
          <TextField label="TRN" value={doc.supplier.trn}
            onChange={(v) => setField('supplier.trn', v)} testId="field-sup-trn" />
          <TextField label="License #" value={doc.supplier.license_number}
            onChange={(v) => setField('supplier.license_number', v)} testId="field-sup-license" />
          <TextField label="Country" value={doc.supplier.country}
            onChange={(v) => setField('supplier.country', v)} testId="field-sup-country" />
          <div className="md:col-span-2">
            <label className="text-[11px] font-semibold text-gray-500 uppercase">Address</label>
            <textarea
              value={doc.supplier.address || ''}
              onChange={(e) => setField('supplier.address', e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="field-sup-address"
            />
          </div>
        </CardContent>
      </Card>

      {/* Line items */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4" /> Line Items ({doc.line_items.length})
          </CardTitle>
          <Button size="sm" variant="outline" onClick={addItem} disabled={locked} data-testid="add-item">
            <Plus className="w-3 h-3 mr-1" /> Add Line
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#023E8A]/10 text-[#023E8A] border-b">
                <th className="p-2 text-left">Product</th>
                <th className="p-2 text-left">SKU</th>
                <th className="p-2 text-right">Qty</th>
                <th className="p-2 text-left">Unit</th>
                <th className="p-2 text-right">Unit Price</th>
                <th className="p-2 text-right">Tax%</th>
                <th className="p-2 text-right">Discount</th>
                <th className="p-2 text-right">Subtotal</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {doc.line_items.map((it, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`line-item-${idx}`}>
                  <td className="p-1 min-w-[220px]">
                    <input className="w-full px-2 py-1 border rounded" value={it.product_name}
                      onChange={(e) => setItem(idx, 'product_name', e.target.value)} />
                  </td>
                  <td className="p-1"><input className="w-24 px-2 py-1 border rounded" value={it.sku || ''}
                      onChange={(e) => setItem(idx, 'sku', e.target.value)} /></td>
                  <td className="p-1"><input type="number" step="0.01" className="w-20 px-2 py-1 border rounded text-right"
                      value={it.quantity} onChange={(e) => setItem(idx, 'quantity', Number(e.target.value))} /></td>
                  <td className="p-1"><input className="w-20 px-2 py-1 border rounded" value={it.unit}
                      onChange={(e) => setItem(idx, 'unit', e.target.value)} /></td>
                  <td className="p-1"><input type="number" step="0.01" className="w-24 px-2 py-1 border rounded text-right"
                      value={it.unit_price} onChange={(e) => setItem(idx, 'unit_price', Number(e.target.value))} /></td>
                  <td className="p-1"><input type="number" step="0.01" className="w-16 px-2 py-1 border rounded text-right"
                      value={it.tax_rate} onChange={(e) => setItem(idx, 'tax_rate', Number(e.target.value))} /></td>
                  <td className="p-1"><input type="number" step="0.01" className="w-20 px-2 py-1 border rounded text-right"
                      value={it.discount} onChange={(e) => setItem(idx, 'discount', Number(e.target.value))} /></td>
                  <td className="p-2 text-right font-mono">{Number(it.subtotal || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-mono font-bold text-[#023E8A]">{Number(it.total || 0).toFixed(2)}</td>
                  <td className="p-1 text-center">
                    <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0"
                      onClick={() => removeItem(idx)} disabled={locked} data-testid={`remove-item-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
              {doc.line_items.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-gray-400">No line items.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-amber-50 text-[#2B2D42] font-bold">
                <td className="p-2" colSpan={7}>Totals ({doc.totals?.currency || 'AED'})</td>
                <td className="p-2 text-right font-mono">{totals.subtotal.toFixed(2)}</td>
                <td className="p-2 text-right font-mono">{totals.grand.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Confirm section */}
      {!locked && (
        <Card className="mb-6 border-emerald-200 bg-emerald-50/40">
          <CardHeader><CardTitle className="text-base text-emerald-900">Confirm &amp; Persist</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-semibold text-gray-600 uppercase">Target Warehouse</label>
                <select
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm bg-white"
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  data-testid="warehouse-select"
                >
                  <option value="">Auto (first active / create virtual)</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name} — {w.location}</option>)}
                </select>
              </div>
              <div className="text-xs text-emerald-900 flex items-start gap-2 p-3 bg-white rounded-lg border">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600" />
                Confirmation auto-creates the supplier (if new) and inventory items for every line. This action cannot be undone.
              </div>
            </div>

            {/* Optional Draft PO */}
            <div className="mt-4 p-4 border rounded-lg bg-white">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={createDraftPO}
                  onChange={(e) => setCreateDraftPO(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="toggle-create-po"
                />
                <span className="text-sm font-semibold text-[#023E8A]">Also create a Draft Purchase Order</span>
                <span className="text-[11px] text-gray-500">(PO → GRN → Invoice cycle)</span>
              </label>

              {createDraftPO && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-gray-600 uppercase">Issuing Company *</label>
                    <select
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm bg-white"
                      value={poCompanyId}
                      onChange={(e) => { setPoCompanyId(e.target.value); setPoBankAccountId(''); }}
                      data-testid="po-company-select"
                    >
                      <option value="">Select company…</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-gray-600 uppercase">Bank Account *</label>
                    <select
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm bg-white"
                      value={poBankAccountId}
                      onChange={(e) => setPoBankAccountId(e.target.value)}
                      disabled={!poCompanyId}
                      data-testid="po-bank-select"
                    >
                      <option value="">Select bank account…</option>
                      {banks.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.bank_name} — {b.account_number || b.iban} ({b.currency})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[11px] font-semibold text-gray-600 uppercase">Delivery Address (optional)</label>
                    <input
                      type="text"
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                      value={poDeliveryAddress}
                      onChange={(e) => setPoDeliveryAddress(e.target.value)}
                      placeholder="e.g. Warehouse A, Jebel Ali FZ, Dubai"
                      data-testid="po-delivery-address"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Post-confirm summary */}
      {locked && (
        <Card className="mb-6 border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4 pb-4 text-sm text-emerald-900">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <CheckCircle2 className="w-4 h-4" /> Persisted
            </div>
            <p>Supplier ID: <code>{doc.matched_supplier_id || doc.created_supplier_id || '—'}</code></p>
            <p>Inventory items created: {doc.created_inventory_ids?.length || 0}</p>
            {doc.created_po_id && (
              <p>
                Draft PO:{' '}
                <Link
                  to={`/admin/procurement/purchase-orders/${doc.created_po_id}`}
                  className="font-semibold underline hover:text-emerald-700"
                  data-testid="link-created-po"
                >
                  {doc.created_po_number || doc.created_po_id}
                </Link>
              </p>
            )}
            <p>Confirmed at: {doc.confirmed_at}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupplierOCRDetail;
